/**
 * Claude Vision Integration for IXVerify
 * Calls the platform IXVerify API (POST /api/ixverify/extract-ids), which ports the
 * former Supabase `extract-ids` edge function verbatim (direct Anthropic call,
 * kill-switched behind IXVERIFY_ENABLED). See ../lib/ixverifyApi.ts.
 */

import { invokeExtractIds } from './ixverifyApi';

export interface ExtractedId {
  value: string;
  confidence: 'high' | 'medium' | 'low';
  context?: string;
}

export interface ExtractionResult {
  success: boolean;
  ids: ExtractedId[];
  rawText?: string;
  error?: string;
}

type ExtractionType =
  | 'manifest'
  | 'manifest_protocol'
  | 'manifest_with_template'
  | 'vial'
  | 'vial_multi'
  | 'ixrs'
  | 'ixrs_multi'
  | 'ixrs_subject'
  | 'ixrs_protocol'
  | 'ixrs_find_matching'
  | 'vial_with_format'
  | 'ixrs_all_fields';

// Response type for all-fields extraction
export interface AllFieldsExtractionResult {
  success: boolean;
  protocolId: ExtractedId | null;
  subjectId: ExtractedId | null;
  medId: ExtractedId | null;
  isTeachingMode: boolean;
  rawText?: string;
  error?: string;
}

// Template data to pass to extraction
export interface ExtractionTemplateData {
  field_hints?: Record<string, string>;
  training_examples?: Array<{
    protocol_id: string;
    subject_id: string;
    med_id: string;
  }>;
}

// Response type for manifest protocol extraction
export interface ManifestExtractionResult {
  success: boolean;
  protocolId: string | null;
  ids: ExtractedId[];
  rawText?: string;
  error?: string;
}

// Template data for manifest extraction
export interface ManifestTemplateData {
  manifest_extraction_prompt?: string;
  manifest_training_data?: Array<{
    manifest_ids: string[];
    protocol_id: string;
  }>;
  field_hints?: Record<string, string>;
}

/**
 * Extract drug IDs from a manifest image using Claude Vision
 */
export async function extractManifestIds(base64Image: string): Promise<ExtractionResult> {
  return extractIds(base64Image, 'manifest');
}

/**
 * Extract protocol ID and drug IDs from a manifest image.
 * This is used for auto-detecting the protocol from the manifest.
 */
export async function extractManifestWithProtocol(
  base64Image: string,
  template?: ManifestTemplateData | null
): Promise<ManifestExtractionResult> {
  try {
    console.log('[claudeVision] Extracting manifest with protocol, image length:', base64Image.length);
    const { data, error } = await invokeExtractIds({
      image: base64Image,
      type: 'manifest_protocol',
      template: template || undefined
    });

    console.log('[claudeVision] Manifest protocol response - data:', data, 'error:', error);

    if (error) {
      console.error('[claudeVision] Edge function error:', error);
      return {
        success: false,
        protocolId: null,
        ids: [],
        error: error.message || 'Failed to call extraction service'
      };
    }

    return data as ManifestExtractionResult;

  } catch (err) {
    console.error('Manifest extraction error:', err);
    return {
      success: false,
      protocolId: null,
      ids: [],
      error: err instanceof Error ? err.message : 'Unknown error occurred'
    };
  }
}

/**
 * Extract manifest IDs using a learned template for better accuracy
 */
export async function extractManifestIdsWithTemplate(
  base64Image: string,
  template: ManifestTemplateData
): Promise<ExtractionResult> {
  try {
    const { data, error } = await invokeExtractIds({
      image: base64Image,
      type: 'manifest_with_template',
      template
    });

    if (error) {
      console.error('[claudeVision] Edge function error:', error);
      return {
        success: false,
        ids: [],
        error: error.message || 'Failed to call extraction service'
      };
    }

    return data as ExtractionResult;

  } catch (err) {
    console.error('Template manifest extraction error:', err);
    return {
      success: false,
      ids: [],
      error: err instanceof Error ? err.message : 'Unknown error occurred'
    };
  }
}

/**
 * Extract drug ID from a vial/label image using Claude Vision
 */
export async function extractVialId(base64Image: string): Promise<ExtractionResult> {
  return extractIds(base64Image, 'vial');
}

/**
 * Extract Med ID from an IXRS document using Claude Vision
 */
export async function extractIxrsMedId(base64Image: string): Promise<ExtractionResult> {
  return extractIds(base64Image, 'ixrs');
}

/**
 * Extract Subject ID from an IXRS document
 */
export async function extractIxrsSubjectId(base64Image: string): Promise<ExtractionResult> {
  return extractIds(base64Image, 'ixrs_subject');
}

/**
 * Extract Protocol ID from an IXRS document
 */
export async function extractIxrsProtocolId(base64Image: string): Promise<ExtractionResult> {
  return extractIds(base64Image, 'ixrs_protocol');
}

/**
 * Extract ALL drug/Med IDs from an IXRS document
 */
export async function extractIxrsDrugIds(base64Image: string): Promise<ExtractionResult> {
  return extractIds(base64Image, 'ixrs_multi');
}

/**
 * Extract IDs from multiple vials in a single image
 */
export async function extractMultipleVialIds(base64Image: string): Promise<ExtractionResult> {
  return extractIds(base64Image, 'vial_multi');
}

/**
 * Extract additional IXRS drug IDs that match a known format
 */
export async function extractIxrsMatchingIds(
  base64Image: string,
  formatExample: string
): Promise<ExtractionResult> {
  return extractIds(base64Image, 'ixrs_find_matching', undefined, formatExample);
}

/**
 * Extract vial IDs that match a specific format
 */
export async function extractVialIdsWithFormat(
  base64Image: string,
  formatExample: string
): Promise<ExtractionResult> {
  return extractIds(base64Image, 'vial_with_format', undefined, formatExample);
}

/**
 * Extract ALL IXRS fields (Protocol, Subject, Med ID) in a single call.
 * Uses template data if available to improve accuracy.
 * This is the preferred method for the new workflow.
 */
export async function extractAllIxrsFields(
  base64Image: string,
  template?: ExtractionTemplateData | null
): Promise<AllFieldsExtractionResult> {
  try {
    console.log('[claudeVision] Invoking extract-ids function, image length:', base64Image.length);
    const { data, error } = await invokeExtractIds({
      image: base64Image,
      type: 'ixrs_all_fields',
      template: template || undefined
    });

    console.log('[claudeVision] Function response - data:', data, 'error:', error);

    if (error) {
      console.error('[claudeVision] Edge function error:', error);
      return {
        success: false,
        protocolId: null,
        subjectId: null,
        medId: null,
        isTeachingMode: true,
        error: error.message || 'Failed to call extraction service'
      };
    }

    return data as AllFieldsExtractionResult;

  } catch (err) {
    console.error('Extraction error:', err);
    return {
      success: false,
      protocolId: null,
      subjectId: null,
      medId: null,
      isTeachingMode: true,
      error: err instanceof Error ? err.message : 'Unknown error occurred'
    };
  }
}

/**
 * Core extraction function that calls the Edge Function
 */
async function extractIds(
  base64Image: string,
  type: ExtractionType,
  context?: string,
  formatExample?: string
): Promise<ExtractionResult> {
  try {
    const { data, error } = await invokeExtractIds({
      image: base64Image,
      type,
      context,
      formatExample
    });

    if (error) {
      console.error('Edge function error:', error);
      return {
        success: false,
        ids: [],
        error: error.message || 'Failed to call extraction service'
      };
    }

    return data as ExtractionResult;

  } catch (err) {
    console.error('Extraction error:', err);
    return {
      success: false,
      ids: [],
      error: err instanceof Error ? err.message : 'Unknown error occurred'
    };
  }
}

/**
 * Extract IDs from multiple manifest images and combine results
 */
export async function extractFromMultipleManifests(
  images: string[]
): Promise<ExtractionResult> {
  const results = await Promise.all(
    images.map(img => extractManifestIds(img))
  );

  const allIds = new Map<string, ExtractedId>();
  const errors: string[] = [];

  for (const result of results) {
    if (!result.success && result.error) {
      errors.push(result.error);
    }
    for (const id of result.ids) {
      const existing = allIds.get(id.value);
      if (!existing || confidenceScore(id.confidence) > confidenceScore(existing.confidence)) {
        allIds.set(id.value, id);
      }
    }
  }

  return {
    success: errors.length === 0 || allIds.size > 0,
    ids: Array.from(allIds.values()),
    error: errors.length > 0 ? errors.join('; ') : undefined
  };
}

function confidenceScore(confidence: 'high' | 'medium' | 'low'): number {
  switch (confidence) {
    case 'high': return 3;
    case 'medium': return 2;
    case 'low': return 1;
    default: return 0;
  }
}

// ============================================
// Template-Aware Extraction Functions
// ============================================

export interface TemplateData {
  ixrs_extraction_prompt?: string;
  vial_extraction_prompt?: string;
  field_hints?: Record<string, string>;
  training_data?: Array<{
    protocol_id: string;
    subject_id: string;
    med_id: string;
    vial_id?: string;
  }>;
}

/**
 * Extract all IXRS fields using a template if available.
 * Falls back to generic extraction if no template.
 */
export async function extractIxrsWithTemplate(
  base64Image: string,
  template?: TemplateData | null
): Promise<{
  protocolId: ExtractionResult;
  subjectId: ExtractionResult;
  medId: ExtractionResult;
}> {
  // If we have a template with training examples, build enhanced prompts
  const customContext = template ? buildTemplateContext(template) : undefined;

  // Extract all three fields in parallel
  const [protocolResult, subjectResult, medIdResult] = await Promise.all([
    extractIds(base64Image, 'ixrs_protocol', customContext),
    extractIds(base64Image, 'ixrs_subject', customContext),
    extractIds(base64Image, 'ixrs', customContext)
  ]);

  return {
    protocolId: protocolResult,
    subjectId: subjectResult,
    medId: medIdResult
  };
}

/**
 * Extract vial ID using a template if available.
 */
export async function extractVialWithTemplate(
  base64Image: string,
  template?: TemplateData | null,
  expectedFormat?: string
): Promise<ExtractionResult> {
  // If we have an expected format from IXRS, use it
  if (expectedFormat) {
    return extractVialIdsWithFormat(base64Image, expectedFormat);
  }

  // Otherwise use standard extraction
  return extractVialId(base64Image);
}

/**
 * Build context string from template training data.
 * This provides examples to improve extraction accuracy.
 */
function buildTemplateContext(template: TemplateData): string {
  const parts: string[] = [];

  // Add field hints if available
  if (template.field_hints) {
    const hints = Object.entries(template.field_hints)
      .map(([field, hint]) => `${field}: ${hint}`)
      .join('; ');
    if (hints) {
      parts.push(`Field locations: ${hints}`);
    }
  }

  // Add training examples if available
  if (template.training_data && template.training_data.length > 0) {
    // Get the 3 most recent examples
    const examples = template.training_data.slice(-3);

    const protocolExamples = examples.map(e => e.protocol_id).filter(Boolean);
    const subjectExamples = examples.map(e => e.subject_id).filter(Boolean);
    const medIdExamples = examples.map(e => e.med_id).filter(Boolean);

    if (protocolExamples.length > 0) {
      parts.push(`Example Protocol IDs: ${protocolExamples.join(', ')}`);
    }
    if (subjectExamples.length > 0) {
      parts.push(`Example Subject IDs: ${subjectExamples.join(', ')}`);
    }
    if (medIdExamples.length > 0) {
      parts.push(`Example Med IDs: ${medIdExamples.join(', ')}`);
    }
  }

  return parts.length > 0 ? parts.join('\n') : '';
}
